import React, { useState } from 'react';

import { AttachFile, ChatBubbleOutline, ThumbUp, ThumbUpOutlined } from '@mui/icons-material';
import { Avatar, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { formatDateShortWithClock } from '~/services/TimeService';

import ChatterMentionInput from './ChatterMentionInput';
import { ChatterPost } from './types';
import {
  CHATTER_HASHTAG_CLASS,
  CHATTER_MENTION_CLASS,
  formatFileSize,
  getInitials,
  highlightMentionsAndHashtags,
} from './utils';

const RICH_TEXT_SX = {
  typography: 'body2',
  [`& .${CHATTER_MENTION_CLASS}`]: { color: 'primary.main', fontWeight: 500 },
  [`& .${CHATTER_HASHTAG_CLASS}`]: { color: 'info.main' },
};

interface Props {
  post: ChatterPost;
  currentUserName: string;
  onToggleLike: (postId: string) => void;
  onAddComment: (postId: string, text: string) => void;
}

export default function ChatterPostCard({ post, currentUserName, onToggleLike, onAddComment }: Props) {
  const [commentText, setCommentText] = useState('');
  const commentInputId = `chatter-comment-${post.id}`;

  const handleSubmitComment = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const text = commentText.trim();
    if (!text) {
      return;
    }
    onAddComment(post.id, text);
    setCommentText('');
  };

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar>{getInitials(post.authorName)}</Avatar>
          <Box>
            <Typography variant="subtitle2">
              {post.authorName}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                to {post.audience}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateShortWithClock(post.createdAt)}
            </Typography>
          </Box>
        </Stack>

        <MarkdownPreview
          content={highlightMentionsAndHashtags(post.text)}
          boxProps={{ sx: { ...RICH_TEXT_SX, mt: 1.5 } }}
        />

        {post.attachments && post.attachments.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
            {post.attachments.map((file) => (
              <Chip key={file.id} icon={<AttachFile />} label={`${file.name} (${formatFileSize(file.size)})`} />
            ))}
          </Stack>
        )}

        <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
          <Button
            size="small"
            startIcon={post.isLikedByMe ? <ThumbUp /> : <ThumbUpOutlined />}
            onClick={() => onToggleLike(post.id)}
          >
            Like{post.likeCount > 0 ? ` (${post.likeCount})` : ''}
          </Button>
          <Button
            size="small"
            startIcon={<ChatBubbleOutline />}
            onClick={() => document.getElementById(commentInputId)?.focus()}
          >
            Comment
          </Button>
          {post.comments.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {post.comments.length} comment{post.comments.length === 1 ? '' : 's'}
            </Typography>
          )}
        </Stack>
      </Box>
      <Divider />
      <Stack spacing={1.5} p={2} bgcolor="action.hover">
        {post.comments.map((comment) => (
          <Stack key={comment.id} direction="row" spacing={1.5}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{getInitials(comment.authorName)}</Avatar>
            <Box>
              <Typography variant="subtitle2">{comment.authorName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateShortWithClock(comment.createdAt)}
              </Typography>
              <MarkdownPreview content={highlightMentionsAndHashtags(comment.text)} boxProps={{ sx: RICH_TEXT_SX }} />
            </Box>
          </Stack>
        ))}
        <form onSubmit={handleSubmitComment}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{getInitials(currentUserName)}</Avatar>
            <ChatterMentionInput
              id={commentInputId}
              placeholder="Write a comment..."
              value={commentText}
              onChange={setCommentText}
            />
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}
